import gql from '@/services/gql'
import { mapMemberToShareQuizInput } from '@/constants/teamPermissions'
import { fragmentQuizCollaboratorForTeam } from '@/graphql/fragments/query/quizCollaborators.fragments'

async function shareQuizBatch(member, quizIds = []) {
    const uniqueQuizIds = [...new Set(quizIds)].filter(Boolean)

    if (!member?.email || !uniqueQuizIds.length) {
        return []
    }

    const shareQuiz = async quizId => {
        const input = mapMemberToShareQuizInput({
            ...member,
            permissions: member.permissionsByQuizId?.[quizId] || member.permissions,
        })

        const query = `
            ${fragmentQuizCollaboratorForTeam}

            mutation ShareQuiz(
                $quizId: ID!,
                $email: String!,
                $permissions: QuizCollaboratorPermissionsInput,
                $expiresAt: String
            ) {
                shareQuiz(
                    quiz_id: $quizId,
                    email: $email,
                    permissions: $permissions,
                    expires_at: $expiresAt
                ) {
                    ...fragmentQuizCollaboratorForTeam
                }
            }
        `

        const data = await gql.request({
            query,
            variables: {
                quizId,
                email: input.email,
                permissions: input.permissions,
                expiresAt: input.expires_at,
            },
        })

        return data?.shareQuiz
    }

    return (await Promise.all(uniqueQuizIds.map(shareQuiz))).filter(Boolean)
}
async function quizCollaboratorsBatch(quizIds = []) {
    const uniqueQuizIds = [...new Set(quizIds)].filter(Boolean)

    if (!uniqueQuizIds.length) {
        return []
    }

    const queryParts = uniqueQuizIds.map((quizId, index) => `
        quizCollaboratorsForQuiz${index}: quizCollaborators(
            quiz_id: "${quizId}",
            only_active: false
        ) {
            data {
                ...fragmentQuizCollaboratorForTeam
            }
        }
    `).join('\n')

    const query = `
        ${fragmentQuizCollaboratorForTeam}

        query QuizCollaboratorsBatch {
            ${queryParts}
        }
    `

    const data = await gql.request({ query })

    return Object.values(data || {})
        .flatMap(page => page?.data || [])
}
async function revokeQuizAccessBatch(permissionIds = []) {
    const uniqueIds = [...new Set(permissionIds)].filter(Boolean)

    if (!uniqueIds.length) {
        return []
    }

    const mutationParts = uniqueIds.map((id, index) => `
        revokeQuizAccess${index}: revokeQuizAccess(id: "${id}") {
            ...fragmentQuizCollaboratorForTeam
        }
    `).join('\n')

    const query = `
        ${fragmentQuizCollaboratorForTeam}

        mutation RevokeQuizAccessBatch {
            ${mutationParts}
        }
    `

    const data = await gql.request({ query })

    return Object.values(data || {})
}

async function updateQuizAccessStatusBatch(permissionIds = [], status = 'accepted') {
    const uniqueIds = [...new Set(permissionIds)].filter(Boolean)

    if (!uniqueIds.length) {
        return []
    }

    const mutationParts = uniqueIds.map((id, index) => `
        updateQuizAccessStatus${index}: updateQuizAccessStatus(id: "${id}", status: "${status}") {
            id
            status
            is_active
            user_email
        }
    `).join('\n')

    const query = `
        mutation UpdateQuizAccessStatusBatch {
            ${mutationParts}
        }
    `

    const data = await gql.request({ query })

    return Object.values(data || {})
}

async function fetchIncomingAccesses() {
    const query = `
        query TeamIncomingAccesses {
            quizzes(include_shared: true, limit: 1000, page: 1) {
                data {
                    id
                    name
                    project_id
                    is_shared
                    viewer_permissions {
                        is_owner
                        is_collaborator
                        can_manage_quiz
                        can_copy
                        can_manage_integrations
                        can_view_crm
                        can_share
                        can_transfer
                        can_delete
                        can_publish
                        owner {
                            id
                            email
                            avatar
                            nickname
                        }
                        inviter {
                            id
                            email
                            avatar
                            nickname
                        }
                        collaborator {
                            id
                            email
                            avatar
                            nickname
                        }
                    }
                    project {
                        id
                        name
                        emoji
                    }
                }
            }
        }
    `

    const data = await gql.request({ query })

    return (data?.quizzes?.data || []).filter(quiz => quiz?.is_shared)
}

export default {
    shareQuizBatch,
    quizCollaboratorsBatch,
    revokeQuizAccessBatch,
    updateQuizAccessStatusBatch,
    fetchIncomingAccesses,
}
